import React, { useEffect, useState, useCallback } from 'react';
import { syncDateRange } from '../utils/dateUtils';

const CADENCES = ['DAILY', 'WEEKLY', 'MONTHLY', 'QUARTERLY', 'YEARLY'];

const emptyForm = {
  rule_name: '',
  vendor_id: '',
  category_id: '',
  department_id: '',
  cadence: 'MONTHLY',
  cadence_anchor: '1',
  default_amount: '',
  amount_variance_pct: '10',
  auto_approve: false,
  start_date: new Date().toISOString().slice(0, 10),
  end_date: ''
};

function PmsExpenseRecurring({ token, selectedProperty }) {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...extra
  });

  const loadData = useCallback(async () => {
    if (!selectedProperty?.id) return;
    try {
      const [rRes, cRes, dRes, vRes] = await Promise.all([
        fetch('/api/v1/expense/recurring', { headers: headers() }),
        fetch('/api/v1/expense/categories', { headers: headers() }),
        fetch('/api/v1/expense/departments', { headers: headers() }),
        fetch('/api/v1/expense/vendors', { headers: headers() })
      ]);
      const [rData, cData, dData, vData] = await Promise.all([rRes.json(), cRes.json(), dRes.json(), vRes.json()]);
      if (rRes.ok) setRows(rData.rows || []);
      if (cRes.ok) setCategories(cData.rows || []);
      if (dRes.ok) setDepartments(dData.rows || []);
      if (vRes.ok) setVendors(vData.rows || []);
      setError('');
    } catch { setError('Network error'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  // syncDateRange: an End before the Start is never valid — the other end follows.
  const set = (k, v) => setForm(f => syncDateRange({ ...f, [k]: v }, 'start_date', 'end_date', k));

  const save = async () => {
    if (!form.rule_name || !form.category_id || !form.department_id || !form.default_amount) return setError('Name, category, department, amount required');
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        ...form,
        vendor_id: form.vendor_id || null,
        category_id: Number(form.category_id),
        department_id: Number(form.department_id),
        default_amount: Number(form.default_amount),
        amount_variance_pct: Number(form.amount_variance_pct) || 10,
        end_date: form.end_date || null
      };
      const res = await fetch('/api/v1/expense/recurring', {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Save failed');
      setMessage('Recurring rule created.');
      setForm(emptyForm);
      loadData();
    } catch { setError('Network error'); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    if (!window.confirm('Manually run the recurring job? This will generate missed DRAFT expenses for all due rules.')) return;
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/v1/expense/recurring/run', {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: '{}'
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Run failed');
      setMessage(`Generated ${data.generated_count || 0} expense(s).`);
      loadData();
    } catch { setError('Network error'); }
  };

  const inputStyle = { padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem' };
  const labelStyle = { display: 'grid', gap: '0.25rem', fontSize: '0.8rem' };
  const fmtMoney = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN')}`;

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.8rem' }}>Recurring Expenses</h2>
      {!selectedProperty?.id && <div className="error-banner">Select a property first.</div>}
      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '0.8rem' }}>{error}</div>}
      {message && <div style={{ background: '#dcfce7', color: '#166534', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '0.8rem' }}>{message}</div>}

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem', marginBottom: '0.8rem' }}>
        <h3 style={{ margin: '0 0 0.7rem 0', fontSize: '1rem' }}>New Recurring Rule</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.7rem' }}>
          <label style={labelStyle}><span>Rule Name *</span><input value={form.rule_name} onChange={e => set('rule_name', e.target.value)} style={inputStyle} /></label>
          <label style={labelStyle}><span>Vendor</span>
            <select value={form.vendor_id} onChange={e => set('vendor_id', e.target.value)} style={inputStyle}>
              <option value="">— None —</option>
              {vendors.map(v => <option key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</option>)}
            </select>
          </label>
          <label style={labelStyle}><span>Category *</span>
            <select value={form.category_id} onChange={e => set('category_id', e.target.value)} style={inputStyle}>
              <option value="">— Select —</option>
              {categories.map(c => <option key={c.category_id} value={c.category_id}>{c.category_name}</option>)}
            </select>
          </label>
          <label style={labelStyle}><span>Department *</span>
            <select value={form.department_id} onChange={e => set('department_id', e.target.value)} style={inputStyle}>
              <option value="">— Select —</option>
              {departments.map(d => <option key={d.department_id} value={d.department_id}>{d.dept_name}</option>)}
            </select>
          </label>
          <label style={labelStyle}><span>Cadence *</span>
            <select value={form.cadence} onChange={e => set('cadence', e.target.value)} style={inputStyle}>
              {CADENCES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label style={labelStyle}><span>Anchor (day-of-month / DoW)</span><input value={form.cadence_anchor} onChange={e => set('cadence_anchor', e.target.value)} style={inputStyle} /></label>
          <label style={labelStyle}><span>Default Amount *</span><input type="number" step="0.01" value={form.default_amount} onChange={e => set('default_amount', e.target.value)} style={inputStyle} /></label>
          <label style={labelStyle}><span>Variance %</span><input type="number" step="0.01" value={form.amount_variance_pct} onChange={e => set('amount_variance_pct', e.target.value)} style={inputStyle} /></label>
          <label style={labelStyle}><span>Start Date *</span><input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} style={inputStyle} /></label>
          <label style={labelStyle}><span>End Date</span><input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} style={inputStyle} /></label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
            <input type="checkbox" checked={!!form.auto_approve} onChange={e => set('auto_approve', e.target.checked)} />
            <span>Auto-approve generated expenses</span>
          </label>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.8rem' }}>
          <button onClick={save} disabled={saving} style={{ padding: '0.5rem 1.2rem', border: 'none', borderRadius: 6, background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>{saving ? 'Saving…' : 'Create Rule'}</button>
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ padding: '0.6rem 1rem', borderBottom: '1px solid #e5e7eb', background: '#fafafa', display: 'flex', alignItems: 'center' }}>
          <span style={{ fontWeight: 600 }}>{rows.length} rule(s)</span>
          <button onClick={runNow} style={{ marginLeft: 'auto', padding: '0.4rem 0.8rem', border: '1px solid #0ea5e9', borderRadius: 6, background: '#0ea5e9', color: '#fff', cursor: 'pointer', fontSize: '0.8rem' }}>Run Catch-Up Now</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
                <th style={{ padding: '0.55rem' }}>Rule</th>
                <th style={{ padding: '0.55rem' }}>Cadence</th>
                <th style={{ padding: '0.55rem' }}>Anchor</th>
                <th style={{ padding: '0.55rem', textAlign: 'right' }}>Amount</th>
                <th style={{ padding: '0.55rem' }}>Next Due</th>
                <th style={{ padding: '0.55rem' }}>Last Run</th>
                <th style={{ padding: '0.55rem' }}>Auto-Approve</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280' }}>No recurring rules yet.</td></tr>
              ) : rows.map(r => (
                <tr key={r.rule_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '0.5rem' }}>{r.rule_name}</td>
                  <td style={{ padding: '0.5rem' }}>{r.cadence}</td>
                  <td style={{ padding: '0.5rem' }}>{r.cadence_anchor || '—'}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmtMoney(r.default_amount)}</td>
                  <td style={{ padding: '0.5rem' }}>{r.next_due_date || '—'}</td>
                  <td style={{ padding: '0.5rem' }}>{r.last_generated_at ? new Date(r.last_generated_at).toLocaleDateString() : '—'}</td>
                  <td style={{ padding: '0.5rem' }}>{r.auto_approve ? '✓' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default PmsExpenseRecurring;
